import React, { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Spin } from 'antd'
import useAuthStore from '@/store/authStore'

interface RouteGuardProps {
  children: React.ReactNode
}

/**
 * Top-level auth guard — redirects unauthenticated users to /login.
 * Waits for the persisted auth state to hydrate before making a decision.
 * Also fetches /me on mount when authenticated but meInfo is missing (e.g. page refresh).
 */
export const RouteGuard: React.FC<RouteGuardProps> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const hasHydrated = useAuthStore((state) => state._hasHydrated)
  const meInfo = useAuthStore((state) => state.meInfo)
  const fetchMe = useAuthStore((state) => state.fetchMe)
  const location = useLocation()

  useEffect(() => {
    if (hasHydrated && isAuthenticated && !meInfo) {
      fetchMe()
    }
  }, [hasHydrated, isAuthenticated, meInfo, fetchMe])

  if (!hasHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spin size="large" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    )
  }

  return <>{children}</>
}

export default RouteGuard
